import React, { useEffect, useMemo, useState } from 'react';
import { CircleDotIcon, ExternalLinkIcon, PauseIcon, PlayIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ChoiceGroup, Toggle } from '../../components/ui/ChoiceGroup';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { useDesignVariants } from '../../contexts/DesignVariantContext';
import { useGovernance } from '../../contexts/GovernanceContext';
import { resolveDesign } from '../../hooks/useDesignVariant';
import { intervalLabel, ROTATION_PRESETS, SURFACES, anchorKey } from '../../data/designVariants';
import type { DesignId, DesignMode, DesignVariantConfig, SurfaceDescriptor } from '../../types/experience';

/**
 * Experience management — design variants.
 *
 * One console controlling every surface that has more than one approved
 * design. Each surface is configured independently: the homepage can be
 * pinned while housing rotates, and neither setting reaches the other.
 *
 * Nothing here publishes on a click. A change is a draft until it is
 * published with a reason, and the reason is written to the audit log.
 */
const MODE_OPTIONS = ['Design 1 only', 'Design 2 only', 'Automatic rotation'] as const;
type ModeLabel = (typeof MODE_OPTIONS)[number];
const MODE_LABELS: Record<DesignMode, ModeLabel> = {
  DESIGN_1: 'Design 1 only',
  DESIGN_2: 'Design 2 only',
  ROTATE: 'Automatic rotation'
};
export function DesignVariants() {
  const {
    loadError,
    source,
    lastReadAt,
    refresh
  } = useDesignVariants();
  const serverBacked = source === 'server';
  return <div className="space-y-6" data-id="element-6235" data-mp="13032i7s0zh19">
      <PageHeader eyebrow="Experience management" title="Design variants" description="Which approved design each Campuzon surface presents. Presentation only — no product data, pricing, verification or permission is affected by anything on this page." data-id="element-6236" data-mp="12yrtntjq3j3a" />

      {loadError && <Panel data-id="element-6237" data-mp="vw4kzy828kxv">
          <div className="flex flex-wrap items-center gap-2" data-id="element-6238" data-mp="vua7v6315x4c">
            <Badge tone="danger" data-id="element-6239" data-mp="19mgfg3s4avs6">Configuration service unavailable</Badge>
          </div>
          <p className="mt-2 text-body text-danger-700" data-id="element-6240" data-mp="14ppyzfcehy68">{loadError}</p>
          <p className="mt-2 max-w-[80ch] text-body-sm text-ink-muted" data-id="element-6241" data-mp="gyuaos75ll3q">
            Visitors are unaffected: each surface continues on the last configuration that was read,
            and falls back to its safe design if it has none. Publishing is refused until the
            service answers, so nothing can be published against a configuration nobody can read.
          </p>
        </Panel>}

      <Panel title="Where this configuration lives" action={<Button size="sm" variant="secondary" onClick={() => refresh()} data-id="element-6243" data-mp="22e9s5l8ywz4m">
            Re-read configuration
          </Button>} data-id="element-6242" data-mp="22c6wakl4wbrz">
        <div className="flex flex-wrap items-center gap-2" data-id="element-6244" data-mp="3f9xmitep5ecv">
          <Badge tone={serverBacked ? 'ok' : 'warn'} data-id="element-6245" data-mp="3ka26itetcr7u">
            {serverBacked ? 'Server-backed — published' : 'This browser only — not published'}
          </Badge>
          {lastReadAt && <span className="text-caption text-ink-subtle" data-id="element-6246" data-mp="3b9drbvdooktd">
              Last read {new Date(lastReadAt).toLocaleTimeString()}
            </span>}
        </div>
        <p className="mt-2 max-w-[80ch] text-body-sm text-ink-muted" data-id="element-6247" data-mp="2udiqku1s4w1">
          {serverBacked ? 'Published configuration is held by the configuration service and read by every session — including a browser that has never visited this console. Open sessions re-read it on a timer, when a tab returns to the foreground, and when connectivity returns, so a publish reaches them without a reload.' : 'No configuration service is configured for this deployment, so what is stored here reaches this browser and its tabs only — another browser, another device or a private window will see the platform defaults. This is a draft, not production state, and the console will not claim otherwise.'}{' '}
          Visitors never see a design named, numbered or announced.
        </p>
        {!serverBacked && <p className="mt-2 max-w-[80ch] text-caption text-ink-subtle" data-id="element-6248" data-mp="2k2ydgz2djimu">
            To make it production configuration, point{' '}
            <code className="text-ink" data-id="element-6249" data-mp="3gth4cbc377jz">VITE_CAMPUZON_API_URL</code> (or{' '}
            <code className="text-ink" data-id="element-6250" data-mp="3crvwj4eetjgn">window.__CAMPUZON_API_URL__</code>) at a service implementing{' '}
            <code className="text-ink" data-id="element-6251" data-mp="380dtmastt8t1">GET /api/config/design-variants</code>,{' '}
            <code className="text-ink" data-id="element-6252" data-mp="331u4k1ixrgwg">PUT /api/admin/design-variants/&#123;surface&#125;</code> and{' '}
            <code className="text-ink" data-id="element-6253" data-mp="4bk8uxvvo9cw">GET /api/admin/design-variants/history</code>. Nothing on
            this screen or on any surface changes when it is connected.
          </p>}
      </Panel>

      <PermissionGate permission="experience.design.manage" label="Publishing design configuration" data-id="element-6254" data-mp="6wi9yktqm5lu">
        <div className="space-y-6" data-id="element-6255" data-mp="bwed6hum1t0c">
          {SURFACES.map(surface => <SurfacePanel key={surface.key} surface={surface} data-id="element-6256" data-mp="gtwp8h3xcock" />)}
        </div>
      </PermissionGate>

      <ChangeHistory data-id="element-6257" data-mp="kw9tt26xqg7r" />
    </div>;
}

/** Ticks once a second so the countdown to the next handover is truthful. */
function useSecond(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/**
 * The publishable settings, with the publish stamp (`updatedAt` / `updatedBy`)
 * deliberately excluded — that stamp describes the last publish, not the
 * configuration being edited.
 */
function settingsOf(config: DesignVariantConfig): string {
  return JSON.stringify([config.mode, config.design1Enabled, config.design2Enabled, config.rotationIntervalMs, config.rotationStartDesign, config.rotationPaused, config.rotationPaused ? config.pausedOn : null, config.fallbackDesign]);
}
function countdown(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor(total % 3600 / 60);
  const seconds = total % 60;
  const pad = (value: number) => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}
function SurfacePanel({
  surface
}: {
  surface: SurfaceDescriptor;
}) {
  const {
    configFor,
    publish,
    source
  } = useDesignVariants();
  const serverBacked = source === 'server';
  const {
    me,
    logAction
  } = useGovernance();
  const live = configFor(surface.key);
  const now = useSecond();
  const [draft, setDraft] = useState<DesignVariantConfig>(live);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // "Unsaved" means the SETTINGS differ. Publishing stamps who published it
  // and when, and those two fields are a record of the publish rather than
  // something the editor holds — comparing them would leave the panel
  // permanently dirty the moment anything was published.
  const dirty = settingsOf(draft) !== settingsOf(live);

  // A publish from elsewhere replaces an untouched draft rather than leaving
  // the editor quietly out of date.
  useEffect(() => {
    if (!dirty) setDraft(live);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // What a visitor arriving now would actually be shown, computed by the same
  // resolver the surfaces use rather than by a second copy of the rules.
  const anchor = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(anchorKey(surface.key));
      const stored = raw ? Number(raw) : NaN;
      return Number.isFinite(stored) ? stored : Date.now();
    } catch {
      return Date.now();
    }
  }, [surface.key]);
  const resolved = resolveDesign(live, anchor, now);
  const currentDesign = surface.designs.find(entry => entry.id === resolved.design);
  const nextDesign = surface.designs.find(entry => entry.id !== resolved.design);
  const invalid = !draft.design1Enabled && !draft.design2Enabled;
  const pinnedToWithdrawn = draft.mode === 'DESIGN_1' && !draft.design1Enabled || draft.mode === 'DESIGN_2' && !draft.design2Enabled;
  async function onPublish() {
    setSaving(true);
    setError(null);
    try {
      const next: DesignVariantConfig = {
        ...draft,
        // Pausing freezes what is actually showing, not the start design.
        pausedOn: draft.rotationPaused ? draft.pausedOn ?? resolved.design : null
      };
      await publish(next, reason.trim(), me.name);
      logAction({
        action: 'Published design configuration',
        resource: 'Design variant',
        resourceId: surface.key,
        before: `${MODE_LABELS[live.mode]}${live.mode === 'ROTATE' ? ` · ${intervalLabel(live.rotationIntervalMs)} · start ${live.rotationStartDesign}${live.rotationPaused ? ' · paused' : ''}` : ''}`,
        after: `${MODE_LABELS[next.mode]}${next.mode === 'ROTATE' ? ` · ${intervalLabel(next.rotationIntervalMs)} · start ${next.rotationStartDesign}${next.rotationPaused ? ' · paused' : ''}` : ''}`,
        reason: reason.trim()
      });
      // The editor now holds exactly what was published, including the paused
      // design the publish resolved, so nothing reads as outstanding.
      setDraft(next);
      setConfirming(false);
      setReason('');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The configuration could not be published.');
    } finally {
      setSaving(false);
    }
  }
  return <Panel title={surface.label} description={surface.description} action={<a href={surface.path} className="inline-flex min-h-touch items-center gap-1.5 rounded-lg px-2 text-body-sm font-medium text-brand-700" data-id="element-6259" data-mp="33we8ixsu8yau">
          Open surface
          <ExternalLinkIcon aria-hidden className="h-4 w-4" data-id="element-6260" data-mp="2c4gm5x5euf7y" />
        </a>} data-id="element-6258" data-mp="2zuauc1esbflv">
      <div className="space-y-6" data-id="element-6261" data-mp="2g6tmg61e1kxc">
        {/* ------------------------------------------------ what is live now */}
        <div className="rounded-xl border border-line bg-canvas p-4" data-id="element-6262" data-mp="2l5wulzco079j">
          <div className="flex flex-wrap items-center gap-2" data-id="element-6263" data-mp="3rlqqam28eeor">
            <Badge tone="ok" data-id="element-6264" data-mp="3rl7bvfbsi8fh">Live</Badge>
            <span className="text-body font-semibold text-ink" data-id="element-6265" data-mp="f5telphwcjt">
              {currentDesign?.name}
            </span>
            <span className="text-body-sm text-ink-muted" data-id="element-6266" data-mp="ajm83br8nnm1">
              ({resolved.design === 'DESIGN_1' ? 'Design 1' : 'Design 2'})
            </span>
          </div>
          <p className="mt-2 text-body-sm text-ink-muted" data-id="element-6267" data-mp="tjhfi7lalqv">
            {live.mode === 'ROTATE' ? live.rotationPaused ? 'Rotation is paused. This design stays until rotation is resumed.' : resolved.nextChangeAt ? `Rotating every ${intervalLabel(live.rotationIntervalMs)}. ${nextDesign?.name} in ${countdown(resolved.nextChangeAt - now)}.` : 'Rotation is configured but only one design is available, so no handover will occur.' : `Pinned. Every visitor sees this design until the configuration changes.`}
          </p>
          <p className="mt-2 text-caption text-ink-subtle" data-id="element-6268" data-mp="1c8g6gs3rxay8">
            Each visitor rotates from their own arrival, so the platform does not turn over at one
            instant. This countdown is for a visitor who arrived when you did.
          </p>
          <p className="mt-2 text-caption text-ink-subtle" data-id="element-6269" data-mp="31oaioqqmlibx">
            Last published {new Date(live.updatedAt).toLocaleString()} by {live.updatedBy}.
          </p>
        </div>

        {/* --------------------------------------------------- the two designs */}
        <div className="grid gap-4 lg:grid-cols-2" data-id="element-6270" data-mp="2wpz6wagrsxr2">
          {surface.designs.map(design => {
          const isEnabled = design.id === 'DESIGN_1' ? draft.design1Enabled : draft.design2Enabled;
          const isLive = resolved.design === design.id;
          return <div key={design.id} className="rounded-xl border border-line p-4" data-id="element-6271" data-mp="3bg6hy54g7ehq">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6272" data-mp="3gehnw4rmh4uo">
                  <span className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-6273" data-mp="3gguh5e2tz1n1">
                    {design.id === 'DESIGN_1' ? 'Design 1' : 'Design 2'}
                  </span>
                  {isLive && <Badge tone={serverBacked ? 'ok' : 'warn'} data-id="element-6274" data-mp="1wiame5s970vy">
                      {serverBacked ? 'Currently live' : 'Presenting here (draft)'}
                    </Badge>}
                  {!isEnabled && <Badge tone="warn" data-id="element-6275" data-mp="26h9r5d9wecdo">Withdrawn</Badge>}
                </div>
                <h3 className="mt-2 text-h4 font-semibold text-ink" data-id="element-6276" data-mp="21hnf375t326g">{design.name}</h3>
                <p className="mt-1 max-w-[52ch] text-body-sm text-ink-muted" data-id="element-6277" data-mp="21fu97u30olzi">{design.summary}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-6278" data-mp="1dpq5zntqqo91">
                  <a href={`${surface.path}?designPreview=${design.id === 'DESIGN_1' ? '1' : '2'}`} target="_blank" rel="noreferrer" className="inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-line-strong px-3 text-body-sm font-medium text-ink" data-id="element-6279" data-mp="18ukmevnt9lo4">
                    Preview the real design
                    <ExternalLinkIcon aria-hidden className="h-4 w-4" data-id="element-6280" data-mp="q28c9mqx1g3f" />
                  </a>
                  <Button size="sm" variant="secondary" disabled={!isEnabled} onClick={() => setDraft(current => ({
                ...current,
                mode: design.id
              }))} data-id="element-6281" data-mp="3pic3sn7gjhr1">
                    Pin this design
                  </Button>
                </div>
                <p className="mt-3 text-caption text-ink-subtle" data-id="element-6282" data-mp="h0663xf6zzxz">
                  Preview opens the actual implemented design for you only. It changes nothing for
                  visitors.
                </p>
                <div className="mt-2 border-t border-line pt-2" data-id="element-6283" data-mp="q1nknbcybtkk">
                  <Toggle label="Available for use" description="Withdrawing a design keeps its code but stops it being presented." checked={isEnabled} onChange={value => setDraft(current => ({
                ...current,
                design1Enabled: design.id === 'DESIGN_1' ? value : current.design1Enabled,
                design2Enabled: design.id === 'DESIGN_2' ? value : current.design2Enabled
              }))} data-id="element-6284" data-mp="2nr5c5xjprxmh" />
                </div>
              </div>;
        })}
        </div>

        {/* ------------------------------------------------------ rendering mode */}
        <div className="space-y-4" data-id="element-6285" data-mp="2u6w1k4sfpu7b">
          <ChoiceGroup label="Rendering mode" size="sm" options={MODE_OPTIONS} value={MODE_LABELS[draft.mode]} onChange={value => setDraft(current => ({
          ...current,
          mode: value === 'Design 1 only' ? 'DESIGN_1' : value === 'Design 2 only' ? 'DESIGN_2' : 'ROTATE'
        }))} data-id="element-6286" data-mp="2u5ue10u8ff8p" />

          {draft.mode === 'ROTATE' && <div className="grid gap-4 rounded-xl border border-line bg-canvas p-4 sm:grid-cols-2" data-id="element-6287" data-mp="16a61lswo3lxf">
              <label className="block" data-id="element-6288" data-mp="1aaouzre7l01q">
                <span className="mb-1.5 block text-body-sm font-medium text-ink" data-id="element-6289" data-mp="1a9n90rvnmt58">
                  Rotation interval
                </span>
                <select value={draft.rotationIntervalMs} onChange={event => setDraft(current => ({
              ...current,
              rotationIntervalMs: Number(event.target.value)
            }))} className="min-h-touch w-full rounded-lg border border-line-strong bg-surface px-3 text-body text-ink" data-id="element-6290" data-mp="11ajrhsc4ylsu">
                  {ROTATION_PRESETS.map(preset => <option key={preset.ms} value={preset.ms} data-id="element-6291" data-mp="3v3eg6b3p70un">
                      {preset.label}
                    </option>)}
                </select>
              </label>

              <label className="block" data-id="element-6292" data-mp="7yyidp06injq">
                <span className="mb-1.5 block text-body-sm font-medium text-ink" data-id="element-6293" data-mp="7zq6kerg5izz">Start with</span>
                <select value={draft.rotationStartDesign} onChange={event => setDraft(current => ({
              ...current,
              rotationStartDesign: event.target.value as DesignId
            }))} className="min-h-touch w-full rounded-lg border border-line-strong bg-surface px-3 text-body text-ink" data-id="element-6294" data-mp="3wk31evpy5x7">
                  <option value="DESIGN_1" data-id="element-6295" data-mp="10nm1pf1db2un">Design 1 — {surface.designs[0].name}</option>
                  <option value="DESIGN_2" data-id="element-6296" data-mp="10l9kj43fowin">Design 2 — {surface.designs[1].name}</option>
                </select>
              </label>

              <div className="sm:col-span-2 flex flex-wrap items-center gap-3 border-t border-line pt-3" data-id="element-6297" data-mp="231i3fq9362ou">
                <Button size="sm" variant="secondary" icon={draft.rotationPaused ? <PlayIcon aria-hidden className="h-4 w-4" data-id="element-6299" data-mp="2h0rfqeu68cnj" /> : <PauseIcon aria-hidden className="h-4 w-4" data-id="element-6300" data-mp="2gydm8af538i3" />} onClick={() => setDraft(current => ({
              ...current,
              rotationPaused: !current.rotationPaused,
              pausedOn: current.rotationPaused ? null : resolved.design
            }))} data-id="element-6298" data-mp="27za2r5c28r32">
                  {draft.rotationPaused ? 'Resume rotation' : 'Pause rotation'}
                </Button>
                <p className="text-body-sm text-ink-muted" data-id="element-6301" data-mp="15l2rzl93f9lo">
                  {draft.rotationPaused ? 'Paused. Whatever is showing when you publish stays showing.' : 'Running. Each design is presented for the interval above.'}
                </p>
              </div>
            </div>}

          <label className="block max-w-sm" data-id="element-6302" data-mp="o2kwflbyr0kt">
            <span className="mb-1.5 block text-body-sm font-medium text-ink" data-id="element-6303" data-mp="k2anbzsvju1q">Fallback design</span>
            <select value={draft.fallbackDesign} onChange={event => setDraft(current => ({
            ...current,
            fallbackDesign: event.target.value as DesignId
          }))} className="min-h-touch w-full rounded-lg border border-line-strong bg-surface px-3 text-body text-ink" data-id="element-6304" data-mp="f37iafcw9ntr">
              <option value="DESIGN_1" data-id="element-6305" data-mp="3shudgpy90wck">Design 1 — {surface.designs[0].name}</option>
              <option value="DESIGN_2" data-id="element-6306" data-mp="3sjwzz00707cj">Design 2 — {surface.designs[1].name}</option>
            </select>
            <span className="mt-1.5 block text-caption text-ink-subtle" data-id="element-6307" data-mp="2cfkq3rob4g79">
              Used if configuration cannot be read, a design is withdrawn, or a design fails while
              rendering. A surface never goes blank.
            </span>
          </label>
        </div>

        {/* ------------------------------------------------- state it must keep */}
        <div className="rounded-xl border border-line p-4" data-id="element-6308" data-mp="1yf9ygmf6axl1">
          <h3 className="text-body font-semibold text-ink" data-id="element-6309" data-mp="1th8g63pkn2am">A handover never disturbs</h3>
          <ul className="mt-2 space-y-1.5" data-id="element-6310" data-mp="1oq8k10oa0o96">
            {surface.preservedState.map(item => <li key={item} className="flex gap-2 text-body-sm text-ink-muted" data-id="element-6311" data-mp="1ko55u4a835k7">
                <CircleDotIcon aria-hidden className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-700" data-id="element-6312" data-mp="1km0punz1ug3k" />
                {item}
              </li>)}
          </ul>
          <p className="mt-3 text-caption text-ink-subtle" data-id="element-6313" data-mp="1appfi3j0s0br">
            Both designs read the same state and the same records. Changing the design changes the
            renderer, not the page — nothing reloads and nobody is navigated.
          </p>
        </div>

        {/* -------------------------------------------------------- publishing */}
        {invalid && <p className="text-body-sm text-danger-700" data-id="element-6314" data-mp="15l3n13q3wqo9">
            Both designs are withdrawn. A surface must have at least one design available, so this
            cannot be published.
          </p>}
        {pinnedToWithdrawn && !invalid && <p className="text-body-sm text-danger-700" data-id="element-6315" data-mp="cuv5ne3eaaaq">
            You have pinned a design that is withdrawn. Make it available, or choose the other
            design.
          </p>}

        {dirty && <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warn-100 bg-warn-50 p-4" data-id="element-6316" data-mp="rlc5it0dss5i">
            <div className="min-w-0" data-id="element-6317" data-mp="3qajcnvdla9cb">
              <p className="text-body font-semibold text-ink" data-id="element-6318" data-mp="3q9i0toor1itj">Unsaved changes</p>
              <p className="text-body-sm text-ink-muted" data-id="element-6319" data-mp="3v94chtoxir08">
                Nothing has changed for visitors yet. Currently in production:{' '}
                {MODE_LABELS[live.mode]}
                {live.mode === 'ROTATE' && ` every ${intervalLabel(live.rotationIntervalMs)}`}.
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-6320" data-mp="tn6ib2a86u2">
              <Button size="sm" variant="ghost" onClick={() => setDraft(live)} data-id="element-6321" data-mp="3s0g2n0i7xfrq">
                Discard changes
              </Button>
              <Button size="sm" variant="primary" disabled={invalid || pinnedToWithdrawn} onClick={() => setConfirming(true)} data-id="element-6322" data-mp="34bncxls6w45k">
                Save &amp; publish
              </Button>
            </div>
          </div>}
      </div>

      <Modal open={confirming} onClose={() => setConfirming(false)} title={`Publish ${surface.label} configuration?`} description="Visitors arriving after this is published are presented according to the new configuration. Anyone reading the page now keeps what they are reading until their next handover." footer={<>
            <Button variant="ghost" onClick={() => setConfirming(false)} data-id="element-6324" data-mp="1146ao51znkhj">
              Cancel
            </Button>
            <Button variant="primary" loading={saving} disabled={reason.trim().length < 8} onClick={onPublish} data-id="element-6325" data-mp="n4n1ra1cs546">
              Publish
            </Button>
          </>} data-id="element-6323" data-mp="39cb6of7syz3h">
        <div className="space-y-4" data-id="element-6326" data-mp="1jtckt3opv3rq">
          <div className="rounded-lg border border-line bg-canvas p-3 text-body-sm" data-id="element-6327" data-mp="1jskxeg59wccj">
            <p className="text-ink-muted" data-id="element-6328" data-mp="160wqqsdsmg2y">
              <span className="font-medium text-ink" data-id="element-6329" data-mp="1a39ujb8zc3x3">Current production:</span>{' '}
              {MODE_LABELS[live.mode]}
              {live.mode === 'ROTATE' && ` · every ${intervalLabel(live.rotationIntervalMs)} · start ${live.rotationStartDesign === 'DESIGN_1' ? 'Design 1' : 'Design 2'}${live.rotationPaused ? ' · paused' : ''}`}
            </p>
            <p className="mt-1.5 text-ink-muted" data-id="element-6330" data-mp="ewu0aor0gggv">
              <span className="font-medium text-ink" data-id="element-6331" data-mp="9vmv8xg95vdw">Proposed:</span> {MODE_LABELS[draft.mode]}
              {draft.mode === 'ROTATE' && ` · every ${intervalLabel(draft.rotationIntervalMs)} · start ${draft.rotationStartDesign === 'DESIGN_1' ? 'Design 1' : 'Design 2'}${draft.rotationPaused ? ' · paused' : ''}`}
            </p>
          </div>

          <label className="block" data-id="element-6332" data-mp="xnv01ezygg1h">
            <span className="mb-1.5 block text-body-sm font-medium text-ink" data-id="element-6333" data-mp="smxgaxzardxv">
              Why is this changing?
            </span>
            <textarea value={reason} onChange={event => setReason(event.target.value)} rows={3} className="w-full rounded-lg border border-line-strong bg-surface p-3 text-body text-ink" placeholder="Recorded against your name in the audit log." data-id="element-6334" data-mp="numj42vxy690" />
            <span className="mt-1.5 block text-caption text-ink-subtle" data-id="element-6335" data-mp="1bjf8thlyzhv6">
              A reason of substance is required. It is stored with the before and after values.
            </span>
          </label>

          {error && <p className="text-body-sm text-danger-700" data-id="element-6336" data-mp="338y3369vmc2h">{error}</p>}
        </div>
      </Modal>
    </Panel>;
}
function ChangeHistory() {
  const {
    changes
  } = useDesignVariants();
  return <Panel title="Configuration history" description="Every published change, with who published it and why." data-id="element-6337" data-mp="385oirshrr5nd">
      {changes.length === 0 ? <p className="text-body-sm text-ink-muted" data-id="element-6338" data-mp="3i4xil1zbfehn">
          Nothing has been published from this console yet. Both surfaces are on their platform
          default configuration.
        </p> : <ul className="space-y-3" data-id="element-6339" data-mp="3qy0uh5ubbja6">
          {changes.map(change => {
        const surface = SURFACES.find(entry => entry.key === change.surface);
        return <li key={change.id} className="rounded-xl border border-line p-4" data-id="element-6340" data-mp="4sdms2107bg8">
                <div className="flex flex-wrap items-center gap-2" data-id="element-6341" data-mp="20txgxkaezuop">
                  <span className="text-body font-semibold text-ink" data-id="element-6342" data-mp="20s4ekh6u3wmi">{surface?.label}</span>
                  <Badge plain data-id="element-6343" data-mp="1vtj9o4gn0orq">{new Date(change.at).toLocaleString()}</Badge>
                </div>
                <p className="mt-1.5 text-body-sm text-ink-muted" data-id="element-6344" data-mp="2am3agygbek60">
                  {MODE_LABELS[change.before.mode]} → {MODE_LABELS[change.after.mode]}
                  {change.after.mode === 'ROTATE' && ` · every ${intervalLabel(change.after.rotationIntervalMs)}`}
                </p>
                <p className="mt-1.5 text-body-sm text-ink" data-id="element-6345" data-mp="2tj4heotfiucl">“{change.reason}”</p>
                <p className="mt-1 text-caption text-ink-subtle" data-id="element-6346" data-mp="2th1sy97xd99z">Published by {change.by}.</p>
              </li>;
      })}
        </ul>}
    </Panel>;
}